import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { postSignInPath } from '../../features/organisations/pendingJoinCode';

// Loading component for auth check
const AuthLoadingSpinner = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, var(--iv-band) 0%, var(--iv-band) 100%)',
    color: 'var(--iv-on-band)'
  }}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid color-mix(in srgb, var(--iv-accent) 30%, transparent)',
        borderTop: '3px solid var(--iv-accent)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <p style={{ margin: 0, fontSize: '16px', color: 'var(--iv-muted)' }}>
        Loading...
      </p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, requiresAuth = true, redirectTo = '/login' }) => {
  const { isAuthenticated, isLoading, user, sessionExpired } = useAuth();
  const location = useLocation();
  const hasAccessToken = Boolean(
    (typeof window !== 'undefined' &&
      (localStorage.getItem('pathbuddy_access_token') ||
        sessionStorage.getItem('pathbuddy_access_token'))) ||
      false
  );

  // Show loading spinner while checking auth status
  if (isLoading) {
    return <AuthLoadingSpinner />;
  }

  // While the session-expired overlay is up, do not Navigate. Clearing tokens
  // leaves isAuthenticated true briefly; bouncing /chat ↔ /login causes a
  // redirect loop that React surfaces as "Oops! Something went wrong" —
  // especially common on mobile/tablet after background refresh failures.
  if (sessionExpired) {
    return children;
  }

  // If route requires auth and user is not authenticated, redirect to login
  if (requiresAuth && (!isAuthenticated || !hasAccessToken)) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Forced password change sits in front of onboarding and the signed-in
  // /login bounce. The destination page does not mount. Onboarding runs
  // only after the flag is clear.
  if (user?.must_change_password) {
    if (location.pathname !== '/set-password') {
      return <Navigate to="/set-password" replace />;
    }
    return children;
  }

  if (
    isAuthenticated
    && !user?.must_change_password
    && location.pathname === '/set-password'
  ) {
    const isCertAdmin = user?.role === 'certadmin';
    return (
      <Navigate
        to={postSignInPath(isCertAdmin ? '/cert-portal' : '/chat')}
        replace
      />
    );
  }

  // If user is authenticated but has not completed onboarding
  const needsOnboarding = sessionStorage.getItem('needs_onboarding') === 'true';
  const hasCompletedOnboarding = Boolean(user?.primary_subject && user?.primary_subject !== 'undecided');

  if (hasCompletedOnboarding && needsOnboarding) {
    sessionStorage.removeItem('needs_onboarding');
  }

  // Only route to onboarding if user is fully authenticated and explicitly flagged for onboarding.
  // Onboarding now renders inline inside /chat (ChatShell), not a separate
  // page — redirect straight there instead of through the now-empty
  // /onboarding route (which itself just redirects to /chat), avoiding a
  // double-redirect hop.
  if (requiresAuth && isAuthenticated && needsOnboarding && !hasCompletedOnboarding && location.pathname !== '/chat') {
    return <Navigate to="/chat" state={{ from: location, ...location.state }} replace />;
  }

  // Instructors are not fenced out of the student experience: they have access
  // to every app, the same as any other account, and reach the instructor
  // dashboard from the profile menu when they want it.

  // If route doesn't require auth and user is authenticated, redirect appropriately
  if (!requiresAuth && isAuthenticated && location.pathname === '/login') {
    // Same default as LoginPage.js's handleLoginSuccess — every account lands
    // on /chat, instructors included — unless a school join link is pending.
    // This fires as soon as sign-up or login authenticates, ahead of
    // LoginPage's own `state.from` redirect, so it must honour the join too.
    return <Navigate to={postSignInPath('/chat')} replace />;
  }

  return children;
};

export default ProtectedRoute;